import React, { Component, ErrorInfo, ReactNode } from 'react';
import Icon from '@/components/ui/Icon';
import { t } from '../i18n/t';
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/core/chunkReload';

interface Props  { children: ReactNode; fallback?: ReactNode; }
interface State  { hasError: boolean; error: Error | null; reloading: boolean; }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, reloading: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Round 132b (A): a lazy route chunk that no longer exists (the app was deployed while this page was open) is cured by ONE automatic reload on the same route;
    // if that was already tried a moment ago the card below is shown.
    if (isChunkLoadError(error)) this.setState({ reloading: reloadOnceForStaleChunk() });
  }

  // React.lazy remembers a failed import, so resetting state would just fail again: a stale-chunk card reloads the page instead.
  handleReset = () => {
    if (isChunkLoadError(this.state.error)) { window.location.reload(); return; }
    this.setState({ hasError: false, error: null, reloading: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.reloading) return <div className="min-h-screen bg-paper" aria-busy="true" data-testid="chunk-reloading" />;

    if (this.props.fallback) return this.props.fallback;

    const stale = isChunkLoadError(this.state.error);

    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-4">
        <div className="bg-card rounded-2xl shadow-card p-8 max-w-md w-full text-center">
          <div className="mb-4 flex justify-center"><Icon name="warning" tile size="lg" /></div>
          <h2 className="text-lg font-semibold text-ink mb-2">{t('errors:errorBoundary.somethingWentWrong')}</h2>
          <p className="text-sm text-subtle mb-6">
            {stale ? t('errors:errorBoundary.appUpdated') : (this.state.error?.message || t('errors:errorBoundary.anUnexpectedErrorOccurred'))}
          </p>
          <button
            onClick={this.handleReset}
            className="bg-amber text-on-amber px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-amber-hover"
          >{stale ? t('errors:errorBoundary.reloadPage') : t('errors:errorBoundary.tryAgain')}</button>
        </div>
      </div>
    );
  }
}
